import Swal from 'sweetalert2';

interface ConfirmOptions {
  title?: string;
  text?: string;
  confirmText?: string;
}

const DELETE_ICON_SVG = `
  <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;display:flex;align-items:center;justify-content:center;margin:0 auto 4px">
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  </div>`;

const CONFIRM_ICON_SVG = `
  <div style="width:56px;height:56px;border-radius:50%;background:#ede9fe;display:flex;align-items:center;justify-content:center;margin:0 auto 4px">
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#4b2789" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  </div>`;

const WARNING_ICON_SVG = `
  <div style="width:56px;height:56px;border-radius:50%;background:#fef3c7;display:flex;align-items:center;justify-content:center;margin:0 auto 4px">
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3.04h16.94a2 2 0 0 0 1.71-3.04L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  </div>`;

const ACTIVE_ICON_SVG = `
  <div style="width:56px;height:56px;border-radius:50%;background:#dcfce7;display:flex;align-items:center;justify-content:center;margin:0 auto 4px">
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  </div>`;

export async function confirmDelete(options: ConfirmOptions = {}): Promise<boolean> {
  const result = await Swal.fire({
    html: `
      ${DELETE_ICON_SVG}
      <div class="swal-title">${options.title ?? 'Delete Record?'}</div>
      <div class="swal-text">${options.text ?? 'This action cannot be undone.'}</div>
    `,
    showCancelButton: true,
    confirmButtonText: options.confirmText ?? 'Yes, Delete',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    showClass: { popup: 'swal-animate-in' },
    hideClass: { popup: '' },
    customClass: {
      popup: 'swal-modern-popup',
      confirmButton: 'swal-btn-danger',
      cancelButton: 'swal-btn-cancel',
      actions: 'swal-actions',
    },
    buttonsStyling: false,
    didOpen: () => { const el = document.querySelector('.swal2-container') as HTMLElement; if (el) el.style.zIndex = '10100'; },
  });
  return result.isConfirmed;
}

export async function swalSuccess(message: string): Promise<void> {
  await Swal.fire({
    html: `
      <div style="width:56px;height:56px;border-radius:50%;background:#f0fdf4;display:flex;align-items:center;justify-content:center;margin:0 auto 4px">
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>
      <div class="swal-title">Success</div>
      <div class="swal-text">${message}</div>
    `,
    showCancelButton: false,
    confirmButtonText: 'OK',
    showClass: { popup: 'swal-animate-in' },
    hideClass: { popup: '' },
    customClass: {
      popup: 'swal-modern-popup',
      confirmButton: 'swal-btn-primary',
      actions: 'swal-actions',
    },
    buttonsStyling: false,
    didOpen: () => { const el = document.querySelector('.swal2-container') as HTMLElement; if (el) el.style.zIndex = '10100'; },
  });
}

export async function swalPermission(message?: string): Promise<void> {
  await Swal.fire({
    html: `
      <div style="width:56px;height:56px;border-radius:50%;background:#eff6ff;display:flex;align-items:center;justify-content:center;margin:0 auto 4px">
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      </div>
      <div class="swal-title">Not Authorised</div>
      <div class="swal-text">${message ?? 'You do not have permission to perform this action.'}</div>
    `,
    showCancelButton: false,
    confirmButtonText: 'OK',
    showClass: { popup: 'swal-animate-in' },
    hideClass: { popup: '' },
    customClass: {
      popup: 'swal-modern-popup',
      confirmButton: 'swal-btn-primary',
      actions: 'swal-actions',
    },
    buttonsStyling: false,
    didOpen: () => { const el = document.querySelector('.swal2-container') as HTMLElement; if (el) el.style.zIndex = '10100'; },
  });
}

export async function swalError(message: string): Promise<void> {
  await Swal.fire({
    html: `
      <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;display:flex;align-items:center;justify-content:center;margin:0 auto 4px">
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      </div>
      <div class="swal-title">Error</div>
      <div class="swal-text">${message}</div>
    `,
    showCancelButton: false,
    confirmButtonText: 'OK',
    showClass: { popup: 'swal-animate-in' },
    hideClass: { popup: '' },
    customClass: {
      popup: 'swal-modern-popup',
      confirmButton: 'swal-btn-danger',
      actions: 'swal-actions',
    },
    buttonsStyling: false,
    didOpen: () => { const el = document.querySelector('.swal2-container') as HTMLElement; if (el) el.style.zIndex = '10100'; },
  });
}

export async function swalConfirm(textOrOptions: string | ConfirmOptions = {}, title?: string, confirmText?: string): Promise<boolean> {
  const opts = typeof textOrOptions === 'string'
    ? { title, text: textOrOptions, confirmText }
    : textOrOptions;
  const result = await Swal.fire({
    html: `
      ${CONFIRM_ICON_SVG}
      <div class="swal-title">${opts.title ?? 'Are you sure?'}</div>
      ${opts.text ? `<div class="swal-text">${opts.text}</div>` : ''}
    `,
    showCancelButton: true,
    confirmButtonText: opts.confirmText ?? 'Yes, Continue',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    showClass: { popup: 'swal-animate-in' },
    hideClass: { popup: '' },
    customClass: {
      popup: 'swal-modern-popup',
      confirmButton: 'swal-btn-primary',
      cancelButton: 'swal-btn-cancel',
      actions: 'swal-actions',
    },
    buttonsStyling: false,
    didOpen: () => { const el = document.querySelector('.swal2-container') as HTMLElement; if (el) el.style.zIndex = '10100'; },
  });
  return result.isConfirmed;
}

export async function confirmAction(options: ConfirmOptions = {}): Promise<boolean> {
  const result = await Swal.fire({
    html: `
      ${CONFIRM_ICON_SVG}
      <div class="swal-title">${options.title ?? 'Are you sure?'}</div>
      ${options.text ? `<div class="swal-text">${options.text}</div>` : ''}
    `,
    showCancelButton: true,
    confirmButtonText: options.confirmText ?? 'Yes, Continue',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    showClass: { popup: 'swal-animate-in' },
    hideClass: { popup: '' },
    customClass: {
      popup: 'swal-modern-popup',
      confirmButton: 'swal-btn-primary',
      cancelButton: 'swal-btn-cancel',
      actions: 'swal-actions',
    },
    buttonsStyling: false,
    didOpen: () => { const el = document.querySelector('.swal2-container') as HTMLElement; if (el) el.style.zIndex = '10100'; },
  });
  return result.isConfirmed;
}

export async function confirmWarning(options: ConfirmOptions = {}): Promise<boolean> {
  const result = await Swal.fire({
    html: `
      ${WARNING_ICON_SVG}
      <div class="swal-title">${options.title ?? 'Are you sure?'}</div>
      ${options.text ? `<div class="swal-text">${options.text}</div>` : ''}
    `,
    showCancelButton: true,
    confirmButtonText: options.confirmText ?? 'Yes, Continue',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    showClass: { popup: 'swal-animate-in' },
    hideClass: { popup: '' },
    customClass: {
      popup: 'swal-modern-popup',
      confirmButton: 'swal-btn-warning',
      cancelButton: 'swal-btn-cancel',
      actions: 'swal-actions',
    },
    buttonsStyling: false,
    didOpen: () => { const el = document.querySelector('.swal2-container') as HTMLElement; if (el) el.style.zIndex = '10100'; },
  });
  return result.isConfirmed;
}

export async function confirmWarningWithHtml(options: ConfirmOptions & { html?: string } = {}): Promise<boolean> {
  const result = await Swal.fire({
    html: `
      ${WARNING_ICON_SVG}
      <div class="swal-title">${options.title ?? 'Are you sure?'}</div>
      ${options.html ? options.html : (options.text ? `<div class="swal-text">${options.text}</div>` : '')}
    `,
    showCancelButton: true,
    confirmButtonText: options.confirmText ?? 'Yes, Continue',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    showClass: { popup: 'swal-animate-in' },
    hideClass: { popup: '' },
    customClass: {
      popup: 'swal-modern-popup',
      confirmButton: 'swal-btn-warning',
      cancelButton: 'swal-btn-cancel',
      actions: 'swal-actions',
    },
    buttonsStyling: false,
    didOpen: () => { const el = document.querySelector('.swal2-container') as HTMLElement; if (el) el.style.zIndex = '10100'; },
  });
  return result.isConfirmed;
}

export async function confirmActive(options: ConfirmOptions = {}): Promise<boolean> {
  const result = await Swal.fire({
    html: `
      ${ACTIVE_ICON_SVG}
      <div class="swal-title">${options.title ?? 'Are you sure?'}</div>
      ${options.text ? `<div class="swal-text">${options.text}</div>` : ''}
    `,
    showCancelButton: true,
    confirmButtonText: options.confirmText ?? 'Yes, Continue',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    showClass: { popup: 'swal-animate-in' },
    hideClass: { popup: '' },
    customClass: {
      popup: 'swal-modern-popup',
      confirmButton: 'swal-btn-active',
      cancelButton: 'swal-btn-cancel',
      actions: 'swal-actions',
    },
    buttonsStyling: false,
    didOpen: () => { const el = document.querySelector('.swal2-container') as HTMLElement; if (el) el.style.zIndex = '10100'; },
  });
  return result.isConfirmed;
}
